import { spring, interpolate } from 'remotion';
import { THEME, getScale, getLayoutMode } from '../../themes/brand';
import { Bot, Sparkles } from 'lucide-react';
import type { BrollComponentProps } from './registry';

interface Message {
  role: 'user' | 'ai';
  text: string;
}

interface ChatProps {
  title?: string;
  messages?: Message[];
}

export const TemplateChat: React.FC<BrollComponentProps> = ({
  frame, fps, width, height, durationInFrames, props,
}) => {
  const s = getScale(height);
  const mode = getLayoutMode(height);
  const data = (props || {}) as ChatProps;
  const dur = durationInFrames;

  const title = data.title || 'AI Assistant';
  const messages: Message[] = data.messages || [
    { role: 'user', text: 'Analyze this content' },
    { role: 'ai', text: 'Processing your request...' },
  ];

  const maxMessages = mode === 'compact' ? 3 : 5;
  const visibleMessages = messages.slice(0, maxMessages);
  const msgStaggerGap = Math.max(8, Math.floor((dur * 0.7) / visibleMessages.length));
  const msgStaggerBase = Math.round(dur * 0.1);
  const titleEntrance = spring({ frame, fps, config: { damping: 16 }, durationInFrames: Math.max(10, Math.round(dur * 0.12)) });

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      backgroundColor: THEME.colors.bg, fontFamily: THEME.font.family,
      padding: THEME.spacing.xl * s,
    }}>
      {/* Header bar with icon-in-pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: THEME.spacing.md * s,
        marginBottom: THEME.spacing.lg * s,
        opacity: titleEntrance,
        paddingBottom: THEME.spacing.md * s,
      }}>
        {/* AI avatar — teal gradient circle with Lucide Bot icon + glow */}
        <div style={{
          width: 44 * s, height: 44 * s, borderRadius: '50%',
          background: `linear-gradient(135deg, ${THEME.colors.accent}, ${THEME.colors.accentHover})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 ${24 * s}px rgba(52,164,159,0.3)`,
        }}>
          <Bot size={22 * s} color={THEME.colors.text} />
        </div>
        <div>
          <div style={{ color: THEME.colors.text, fontSize: THEME.font.subtitle * s, fontWeight: THEME.font.weight.semibold }}>
            {title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 * s, marginTop: 2 * s }}>
            <div style={{
              width: 8 * s, height: 8 * s, borderRadius: '50%',
              backgroundColor: THEME.colors.success,
              boxShadow: `0 0 8px ${THEME.colors.success}80`,
            }} />
            <span style={{ color: THEME.colors.textDim, fontSize: THEME.font.small * s }}>Online</span>
          </div>
        </div>
      </div>

      {/* Gradient accent line */}
      <div style={{
        width: '100%', height: 2 * s,
        background: `linear-gradient(90deg, ${THEME.colors.accent}, transparent)`,
        borderRadius: THEME.radius.pill, marginBottom: THEME.spacing.lg * s,
        opacity: titleEntrance,
      }} />

      {/* Messages */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: THEME.spacing.md * s, justifyContent: 'center' }}>
        {visibleMessages.map((msg, i) => {
          const delay = msgStaggerBase + i * msgStaggerGap;
          const entrance = spring({
            frame: Math.max(0, frame - delay), fps,
            config: { damping: 12, stiffness: 80 },
          });

          const isAI = msg.role === 'ai';
          const typingDots = Math.round(dur * 0.05);
          const typingPhase = frame >= delay && frame < delay + typingDots;
          const dotPulse = Math.sin(frame * 0.4) * 0.5 + 0.5;

          // Scale typing speed so text completes before next message starts
          const availableFrames = Math.max(10, msgStaggerGap * 0.7);
          const charsToShow = isAI
            ? Math.floor(interpolate(frame, [delay + typingDots, delay + typingDots + availableFrames], [0, msg.text.length], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }))
            : msg.text.length;
          const displayText = msg.text.substring(0, charsToShow);

          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start',
              justifyContent: isAI ? 'flex-start' : 'flex-end',
              gap: THEME.spacing.sm * s,
              opacity: entrance,
              transform: `translateY(${interpolate(entrance, [0, 1], [30, 0])}px)`,
            }}>
              {/* AI mini avatar */}
              {isAI && (
                <div style={{
                  width: 28 * s, height: 28 * s, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: THEME.colors.bgSecondary,
                  border: `1px solid ${THEME.colors.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: 4 * s,
                }}>
                  <Sparkles size={14 * s} color={THEME.colors.accent} />
                </div>
              )}

              <div style={{
                maxWidth: '80%',
                padding: `${THEME.spacing.md * s}px ${THEME.spacing.lg * s}px`,
                borderRadius: THEME.radius.lg * s,
                backgroundColor: isAI ? THEME.colors.bgCard : THEME.colors.accent,
                color: THEME.colors.text,
                fontSize: THEME.font.body * s, fontWeight: THEME.font.weight.normal, lineHeight: 1.5,
                border: isAI ? `1px solid ${THEME.colors.border}` : 'none',
                boxShadow: isAI ? '0 2px 12px rgba(0,0,0,0.3)' : `0 2px 12px ${THEME.colors.accent}40`,
              }}>
                {isAI && typingPhase && charsToShow === 0 ? (
                  <div style={{ display: 'flex', gap: 6 * s }}>
                    {[0, 1, 2].map(d => (
                      <div key={d} style={{
                        width: 8 * s, height: 8 * s, borderRadius: '50%',
                        backgroundColor: THEME.colors.textMuted,
                        opacity: d === Math.floor(frame * 0.15) % 3 ? 1 : dotPulse * 0.5 + 0.3,
                      }} />
                    ))}
                  </div>
                ) : displayText}
                {isAI && charsToShow < msg.text.length && charsToShow > 0 && (
                  <span style={{ opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0, color: THEME.colors.accent }}>|</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
